import React, { useState, useEffect } from "react";
import {
  Form,
  Button,
  Table,
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  Modal,
  Badge,
  Accordion,
  ListGroup,
} from "react-bootstrap";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getActionPermissions } from "../../Utils/ActionPermissionChecker";
import { doAuditLog } from "../../Utils/saveHistoryAction";
import { showMyAlert } from "../../Utils/alertService";

const DepartmentManagement = () => {
  // API endpoints
  const API_URL =
    "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollPayslip_PayrollPayslipData_PayrollPayslipLabelsList";

  // State management
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Form states
  const [newDepartment, setNewDepartment] = useState("");
  const [newDesignation, setNewDesignation] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [newEmploymentType, setNewEmploymentType] = useState("");

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentEditItem, setCurrentEditItem] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editDepartment, setEditDepartment] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState("");
  const [confirmModalItem, setConfirmModalItem] = useState("");

  // Active tab state
  const [activeTab, setActiveTab] = useState("departments");
  const companyKey = sessionStorage.getItem("companyKey");

  // Get user info from session storage
  const userRole = sessionStorage.getItem("role");
  const { canView, canAdd, canEdit, canDelete } =
    getActionPermissions(userRole);
  const userId = sessionStorage.getItem("userId");
  const userName = sessionStorage.getItem("userName");
  const userMobile = sessionStorage.getItem("userMobile");

  const navigate = useNavigate();

  // Authentication check
  useEffect(() => {
    const checkAuth = () => {
      const userId = sessionStorage.getItem("userMobile");
      if (!userId) {
        navigate("/signIn");
      }
    };
    checkAuth();
  }, [navigate]);

  const isSuperAdmin = userRole === "superadmin";

  // Fetch data from API
  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_URL);
      const data = response.data;

      // Filter data by companyKey first
      const companyData = data.filter(item => item.companyKey === companyKey);

      // Then filter data by section names within the company's data
      const depts = companyData.filter((item) => item.sectionName === "Department");
      const desigs = companyData.filter((item) => item.sectionName === "Designation");
      const empTypes = companyData.filter(
        (item) => item.sectionName === "EmploymentType"
      );

      setDepartments(depts);
      setDesignations(desigs);
      setEmploymentTypes(empTypes);

      setLoading(false);
    } catch (err) {
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Error",
        message: "Failed to fetch data from server",
        duration: 2500,
      });
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, []);

  // Save data to API
  const saveDataToAPI = async (sectionName, value, departmentId = null) => {
    try {
      setLoading(true);
      const data = {
        sectionName,
        value,
        companyName: "null",
        companyAddress: "null",
        companyLogo: "null",
        // Add department reference for designations
        ...(departmentId && { departmentId: departmentId }),
      };

      const response = await axios.post(API_URL, { ...data, companyKey });
      doAuditLog("Add", "Payslip Configuration", "", data);

      showMyAlert({
        type: "success", // "success" | "danger" | "warning" | "info"
        title: "Success",
        message: `${sectionName} "${value}" added successfully!`,
        duration: 2500, // auto-close in 3s
      });

      // Refresh data
      await fetchData();
      setLoading(false);
      return response.data;
    } catch (err) {
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Error",
        message: `Failed to add ${sectionName.toLowerCase()}`,
        duration: 2500, // auto-close in 3s
      });
      setLoading(false);
      throw err;
    }
  };

  // Update data in API
  const updateDataInAPI = async (
    id,
    sectionName,
    value,
    originalItem,
    departmentId = null,
    currentEditItem
  ) => {
    try {
      setLoading(true);
      const data = {
        sectionName,
        value,
        companyName: "null",
        companyAddress: "null",
        companyLogo: "null",
        // Add department reference for designations
        ...(departmentId && { departmentId: departmentId }),
      };

      const response = await axios.put(`${API_URL}/${id}`, data);
      doAuditLog("Update", "Payslip Configuration", currentEditItem, {
        ...currentEditItem,
        ...data,
      });

      showMyAlert({
        type: "success", // "success" | "danger" | "warning" | "info"
        title: "Success",
        message: `${sectionName} "${value}" updated successfully!`,
        duration: 2500, // auto-close in 3s
      });

      // Refresh data
      await fetchData();
      setLoading(false);
      return response.data;
    } catch (err) {
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Error",
        message: `Failed to update ${sectionName.toLowerCase()}`,
        duration: 2500, // auto-close in 3s
      });

      setLoading(false);
      throw err;
    }
  };

  const deleteDataFromAPI = async (id, item) => {
    try {
      setLoading(true);
      await axios.delete(`${API_URL}/${id}`);

      doAuditLog("Delete", "Payslip Configuration", item, "");
      showMyAlert({
        type: "success", // "success" | "danger" | "warning" | "info"
        title: "Success",
        message: `${item.sectionName} "${item.value}" deleted successfully!`,
        duration: 2500, // auto-close in 3s
      });

      // Refresh data
      await fetchData();
      setLoading(false);
    } catch (err) {
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Error",
        message: `Failed to delete ${item.sectionName.toLowerCase()}`,
        duration: 2500, // auto-close in 3s
      });
      setLoading(false);
    }
  };

  // Handle department submission
  const handleDepartmentSubmit = async (e) => {
    e.preventDefault();
    if (!newDepartment.trim()) {
      showMyAlert({
        type: "warning", // "success" | "danger" | "warning" | "info"
        title: "Validation Error",
        message: "Please enter a department name",
        duration: 2500, // auto-close in 3s
      });
      return;
    }

    // Check if department already exists
    const exists = departments.some(
      (dept) => dept.value.toLowerCase() === newDepartment.trim().toLowerCase()
    );

    if (exists) {
      showMyAlert({
        type: "warning", // "success" | "danger" | "warning" | "info"
        title: "Validation Error",
        message: "Department already exists",
        duration: 2500, // auto-close in 3s
      });

      return;
    }

    try {
      await saveDataToAPI("Department", newDepartment.trim());
      setNewDepartment("");
    } catch (err) {
      console.error("Error adding department:", err);
    }
  };

  const handleDesignationSubmit = async (e) => {
    e.preventDefault();
    if (!newDesignation.trim() || !selectedDepartment) {
      showMyAlert({
        type: "warning", // "success" | "danger" | "warning" | "info"
        title: "Validation Error",
        message: "Please select a department and enter a designation",
        duration: 2500, // auto-close in 3s
      });
      return;
    }

    // Find the selected department
    const selectedDept = departments.find(
      (dept) => dept.value === selectedDepartment
    );
    if (!selectedDept) {
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Error",
        message: "Selected department not found",
        duration: 2500, // auto-close in 3s
      });
      return;
    }

    // Check if designation already exists in the same department
    const exists = designations.some(
      (desig) =>
        desig.value.toLowerCase() === newDesignation.trim().toLowerCase() &&
        desig.departmentId === selectedDept.id
    );

    if (exists) {
      showMyAlert({
        type: "warning", // "success" | "danger" | "warning" | "info"
        title: "Validation Error",
        message: "Designation already exists in this department",
        duration: 2500, // auto-close in 3s
      });
      return;
    }

    try {
      // Create designation with only designation name in value field
      await saveDataToAPI(
        "Designation",
        newDesignation.trim(),
        selectedDept.id
      );
      setNewDesignation("");
      setSelectedDepartment("");
    } catch (err) {
      console.error("Error adding designation:", err);
    }
  };

  // Handle employment type submission
  const handleEmploymentTypeSubmit = async (e) => {
    e.preventDefault();
    if (!newEmploymentType.trim()) {
      showMyAlert({
        type: "warning", // "success" | "danger" | "warning" | "info"
        title: "Validation Error",
        message: "Please enter an employment type",
        duration: 2500, // auto-close in 3s
      });
      return;
    }

    // Check if employment type already exists
    const exists = employmentTypes.some(
      (empType) =>
        empType.value.toLowerCase() === newEmploymentType.trim().toLowerCase()
    );

    if (exists) {
      showMyAlert({
        type: "warning", // "success" | "danger" | "warning" | "info"
        title: "Validation Error",
        message: "Employment type already exists",
        duration: 2500, // auto-close in 3s
      });
      return;
    }

    try {
      await saveDataToAPI("EmploymentType", newEmploymentType.trim());
      setNewEmploymentType("");
    } catch (err) {
      console.error("Error adding employment type:", err);
    }
  };

  const handleEdit = (item) => {
    setCurrentEditItem(item);

    // For designations, just use the value directly (no parsing needed)
    setEditValue(item.value);

    // If it's a designation, find and set the department
    if (item.sectionName === "Designation" && item.departmentId) {
      const dept = departments.find((d) => d.id === item.departmentId);
      setEditDepartment(dept ? dept.value : "");
    } else {
      setEditDepartment("");
    }

    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    if (!editValue.trim()) {
      showMyAlert({
        type: "warning", // "success" | "danger" | "warning" | "info"
        title: "Validation Error",
        message: "Please enter a value",
        duration: 2500, // auto-close in 3s
      });
      return;
    }

    try {
      let finalValue = editValue.trim();
      let departmentId = null;

      // If editing designation and department is selected
      if (currentEditItem.sectionName === "Designation" && editDepartment) {
        const selectedDept = departments.find(
          (dept) => dept.value === editDepartment
        );
        if (selectedDept) {
          departmentId = selectedDept.id;
          // Keep value as just the designation name
          finalValue = editValue.trim();
        }
      }

      await updateDataInAPI(
        currentEditItem.id,
        currentEditItem.sectionName,
        finalValue,
        currentEditItem,
        departmentId,
        currentEditItem
      );
      setShowEditModal(false);
      setCurrentEditItem(null);
      setEditValue("");
      setEditDepartment("");
    } catch (err) {
      console.error("Error updating item:", err);
    }
  };

  const handleDelete = async (item) => {
    // For designations, use the value directly (no parsing needed)
    const itemDisplayName = item.value;

    const confirmMessage = `Are you sure you want to delete "${itemDisplayName}" from ${item.sectionName.toLowerCase()}?`;

    setConfirmModalMessage(confirmMessage);
    setShowConfirmModal(true);
    setConfirmModalItem(item);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteDataFromAPI(confirmModalItem.id, confirmModalItem);
    } catch (err) {
      console.error("Error deleting item:", err);
    } finally {
      setConfirmModalMessage("");
      setShowConfirmModal(false);
    }
  };

  const getDesignationsByDepartment = () => {
    const grouped = {};

    designations.forEach((designation) => {
      let departmentName = "Unassigned";

      // Find department by departmentId
      if (designation.departmentId) {
        const dept = departments.find((d) => d.id === designation.departmentId);
        departmentName = dept ? dept.value : "Unassigned";
      }

      if (!grouped[departmentName]) {
        grouped[departmentName] = [];
      }

      grouped[departmentName].push({
        ...designation,
        displayName: designation.value, // Use value directly
        departmentName: departmentName,
        departmentId: designation.departmentId,
      });
    });

    return grouped;
  };

  const designationsByDepartment = getDesignationsByDepartment();

  return (
    <Container className="container-fluid px-1 py-5">
      {/* <h3 className="mb-4">
        <i className="bi bi-building me-2"></i>
        Department Management
      </h3> */}

      {/* Navigation Tabs */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="p-0" style={{ background: "#1e3147" }}>
          <nav>
            <div
              className="nav nav-tabs border-0 p-2"
              id="nav-tab"
              role="tablist"
            >
              <button
                className={`nav-link border-0 ${activeTab === "departments" ? "active" : ""
                  }`}
                style={{
                  backgroundColor:
                    activeTab === "departments" ? "white" : "transparent",
                  color: activeTab === "departments" ? "#1e3147" : "white",
                  borderRadius: 0,
                  marginRight: "1px",
                  borderTopLeftRadius: "10px",
                  borderTopRightRadius: "10px",
                }}
                onClick={() => setActiveTab("departments")}
                type="button"
              >
                <i className="bi bi-building me-2"></i>
                Departments ({departments.length})
              </button>
              <button
                className={`nav-link border-0 ${activeTab === "designations" ? "active" : ""
                  }`}
                style={{
                  backgroundColor:
                    activeTab === "designations" ? "white" : "transparent",
                  color: activeTab === "designations" ? "#1e3147" : "white",
                  borderRadius: 0,
                  marginRight: "1px",
                  borderTopLeftRadius: "10px",
                  borderTopRightRadius: "10px",
                }}
                onClick={() => setActiveTab("designations")}
                type="button"
              >
                <i className="bi bi-person-badge me-2"></i>
                Designations ({designations.length})
              </button>
              <button
                className={`nav-link border-0 ${activeTab === "employmentTypes" ? "active" : ""
                  }`}
                style={{
                  backgroundColor:
                    activeTab === "employmentTypes" ? "white" : "transparent",
                  color: activeTab === "employmentTypes" ? "#1e3147" : "white",
                  borderRadius: 0,
                  borderTopLeftRadius: "10px",
                  borderTopRightRadius: "10px",
                }}
                onClick={() => setActiveTab("employmentTypes")}
                type="button"
              >
                <i className="bi bi-briefcase me-2"></i>
                Employment Types ({employmentTypes.length})
              </button>
            </div>
          </nav>
        </Card.Header>

        <Card.Body>
          {/* Departments Tab */}
          {activeTab === "departments" && (
            <div>
              <Row>
                <Col md={6}>
                  <Card className="border-primary">
                    <Card.Header className="bg-light">
                      <h6 className="mb-0">
                        <i className="bi bi-plus-circle me-2"></i>
                        Add New Department
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      <Form onSubmit={handleDepartmentSubmit}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">
                            Department Name
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={newDepartment}
                            onChange={(e) => setNewDepartment(e.target.value)}
                            placeholder="Enter department name (e.g., IT, Sales, HR)"
                            disabled={loading}
                            className="border-primary"
                          />
                        </Form.Group>
                        {canAdd && (
                          <div className="d-grid">
                            <Button
                              variant="primary"
                              type="submit"
                              disabled={
                                loading ||
                                !newDepartment.trim() ||
                                !isSuperAdmin
                              }
                              title={
                                !isSuperAdmin
                                  ? "Only superadmins can add departments"
                                  : ""
                              }
                            >
                              {loading ? (
                                <>
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                  />
                                  Adding...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-plus-lg me-2"></i>
                                  Add Department
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </Form>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="border-info">
                    <Card.Header className="bg-light">
                      <h6 className="mb-0">
                        <i className="bi bi-list-ul me-2"></i>
                        Existing Departments
                      </h6>
                    </Card.Header>
                    <Card.Body
                      style={{ maxHeight: "300px", overflowY: "auto" }}
                    >
                      {departments.length > 0 ? (
                        <ListGroup variant="flush">
                          {departments.map((dept, index) => (
                            <ListGroup.Item
                              key={dept.id}
                              className="d-flex justify-content-between align-items-center px-0"
                            >
                              <div>
                                <Badge bg="primary" className="me-2">
                                  {index + 1}
                                </Badge>
                                <strong>{dept.value}</strong>
                                <br />
                                <small className="text-muted">
                                  {designationsByDepartment[dept.value]
                                    ?.length || 0}{" "}
                                  designations
                                </small>
                              </div>
                              <div>
                                {canEdit && (
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => handleEdit(dept)}
                                    disabled={loading}
                                  >
                                    <i className="bi bi-pencil me-1"></i>
                                    Edit
                                  </Button>
                                )}
                                {canDelete && (
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDelete(dept)}
                                    disabled={loading}
                                  >
                                    <i className="bi bi-trash me-1"></i>
                                    Delete
                                  </Button>
                                )}
                              </div>
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      ) : (
                        <div className="text-center text-muted py-4">
                          <i className="bi bi-inbox display-4"></i>
                          <p className="mt-2">No departments added yet</p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          )}

          {/* Designations Tab */}
          {activeTab === "designations" && (
            <div>
              <Row>
                <Col md={6}>
                  <Card className="border-success">
                    <Card.Header className="bg-light">
                      <h6 className="mb-0">
                        <i className="bi bi-plus-circle me-2"></i>
                        Add New Designation
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      <Form onSubmit={handleDesignationSubmit}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">
                            Select Department
                          </Form.Label>
                          <Form.Select
                            value={selectedDepartment}
                            onChange={(e) =>
                              setSelectedDepartment(e.target.value)
                            }
                            disabled={loading}
                            className="border-success"
                          >
                            <option value="">Choose department...</option>
                            {departments.map((dept) => (
                              <option key={dept.id} value={dept.value}>
                                {dept.value}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">
                            Designation Name
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={newDesignation}
                            onChange={(e) => setNewDesignation(e.target.value)}
                            placeholder="Enter designation (e.g., Sales Executive, Software Developer)"
                            disabled={loading}
                            className="border-success"
                          />
                        </Form.Group>

                        {canAdd && (
                          <div className="d-grid">
                            <Button
                              variant="success"
                              type="submit"
                              disabled={
                                loading ||
                                !newDesignation.trim() ||
                                !selectedDepartment ||
                                !isSuperAdmin
                              }
                              title={
                                !isSuperAdmin
                                  ? "Only superadmins can add designations"
                                  : ""
                              }
                            >
                              {loading ? (
                                <>
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                  />
                                  Adding...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-plus-lg me-2"></i>
                                  Add Designation
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </Form>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="border-info">
                    <Card.Header className="bg-light">
                      <h6 className="mb-0">
                        <i className="bi bi-list-ul me-2"></i>
                        Designations by Department
                      </h6>
                    </Card.Header>
                    <Card.Body
                      style={{ maxHeight: "400px", overflowY: "auto" }}
                    >
                      {Object.keys(designationsByDepartment).length > 0 ? (
                        <Accordion>
                          {Object.entries(designationsByDepartment).map(
                            ([departmentName, designationList], deptIndex) => (
                              <Accordion.Item
                                eventKey={deptIndex.toString()}
                                key={departmentName}
                              >
                                <Accordion.Header>
                                  <div className="d-flex justify-content-between align-items-center w-100 me-3">
                                    <strong>{departmentName}</strong>
                                    <Badge bg="success" className="ms-2">
                                      {designationList.length}
                                    </Badge>
                                  </div>
                                </Accordion.Header>
                                <Accordion.Body>
                                  <ListGroup variant="flush">
                                    {designationList.map((desig, index) => (
                                      <ListGroup.Item
                                        key={desig.id}
                                        className="d-flex justify-content-between align-items-center px-0"
                                      >
                                        <div>
                                          <Badge
                                            bg="secondary"
                                            className="me-2"
                                          >
                                            {index + 1}
                                          </Badge>
                                          <strong>{desig.displayName}</strong>
                                        </div>
                                        <div>
                                          {canEdit && (
                                            <Button
                                              variant="outline-primary"
                                              size="sm"
                                              className="me-2"
                                              onClick={() => handleEdit(desig)}
                                              disabled={loading}
                                            >
                                              <i className="bi bi-pencil me-1"></i>
                                              Edit
                                            </Button>
                                          )}
                                          {canDelete && (
                                            <Button
                                              variant="outline-danger"
                                              size="sm"
                                              onClick={() =>
                                                handleDelete(desig)
                                              }
                                              disabled={loading}
                                            >
                                              <i className="bi bi-trash me-1"></i>
                                              Delete
                                            </Button>
                                          )}
                                        </div>
                                      </ListGroup.Item>
                                    ))}
                                  </ListGroup>
                                </Accordion.Body>
                              </Accordion.Item>
                            )
                          )}
                        </Accordion>
                      ) : (
                        <div className="text-center text-muted py-4">
                          <i className="bi bi-inbox display-4"></i>
                          <p className="mt-2">No designations added yet</p>
                          <small>
                            Add departments first, then add designations
                          </small>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          )}

          {/* Employment Types Tab */}
          {activeTab === "employmentTypes" && (
            <div>
              <Row>
                <Col md={6}>
                  <Card className="border-warning">
                    <Card.Header className="bg-light">
                      <h6 className="mb-0">
                        <i className="bi bi-plus-circle me-2"></i>
                        Add New Employment Type
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      <Form onSubmit={handleEmploymentTypeSubmit}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">
                            Employment Type
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={newEmploymentType}
                            onChange={(e) =>
                              setNewEmploymentType(e.target.value)
                            }
                            placeholder="Enter employment type (e.g., Full Time, Part Time, Contract)"
                            disabled={loading}
                            className="border-warning"
                          />
                        </Form.Group>
                        {canAdd && (
                          <div className="d-grid">
                            <Button
                              variant="warning"
                              type="submit"
                              disabled={
                                loading ||
                                !newEmploymentType.trim() ||
                                !isSuperAdmin
                              }
                              title={
                                !isSuperAdmin
                                  ? "Only superadmins can add employment types"
                                  : ""
                              }
                            >
                              {loading ? (
                                <>
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                  />
                                  Adding...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-plus-lg me-2"></i>
                                  Add Employment Type
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </Form>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="border-info">
                    <Card.Header className="bg-light">
                      <h6 className="mb-0">
                        <i className="bi bi-list-ul me-2"></i>
                        Existing Employment Types
                      </h6>
                    </Card.Header>
                    <Card.Body
                      style={{ maxHeight: "300px", overflowY: "auto" }}
                    >
                      {employmentTypes.length > 0 ? (
                        <ListGroup variant="flush">
                          {employmentTypes.map((empType, index) => (
                            <ListGroup.Item
                              key={empType.id}
                              className="d-flex justify-content-between align-items-center px-0"
                            >
                              <div>
                                <Badge
                                  bg="warning"
                                  text="dark"
                                  className="me-2"
                                >
                                  {index + 1}
                                </Badge>
                                <strong>{empType.value}</strong>
                              </div>
                              <div>
                                {canEdit && (
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => handleEdit(empType)}
                                    disabled={loading}
                                  >
                                    <i className="bi bi-pencil me-1"></i>
                                    Edit
                                  </Button>
                                )}
                                {canDelete && (
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDelete(empType)}
                                    disabled={loading}
                                  >
                                    <i className="bi bi-trash me-1"></i>
                                    Delete
                                  </Button>
                                )}
                              </div>
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      ) : (
                        <div className="text-center text-muted py-4">
                          <i className="bi bi-inbox display-4"></i>
                          <p className="mt-2">No employment types added yet</p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <i className="bi bi-pencil-square me-2"></i>
            Edit {currentEditItem?.sectionName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentEditItem?.sectionName === "Designation" && (
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Department</Form.Label>
              <Form.Select
                value={editDepartment}
                onChange={(e) => setEditDepartment(e.target.value)}
                className="border-success"
              >
                <option value="">Choose department...</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.value}>
                    {dept.value}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          <Form.Group>
            <Form.Label className="fw-bold">
              {currentEditItem?.sectionName} Name
            </Form.Label>
            <Form.Control
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              placeholder={`Enter ${currentEditItem?.sectionName.toLowerCase()} name`}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            <i className="bi bi-x-circle me-1"></i>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleEditSubmit}
            disabled={loading || !editValue.trim()}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-1"></i>
                Update
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showConfirmModal}
        onHide={() => {
          setShowConfirmModal(false);
          setConfirmModalMessage("");
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{confirmModalMessage}</p>
          <p className="text-danger">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowConfirmModal(false);
              setConfirmModalMessage("");
            }}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Confirm Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Loading Overlay */}
      {loading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 9999,
          }}
        >
          <div className="bg-white p-4 rounded shadow text-center">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <h5 className="mb-2">Processing...</h5>
            <p className="text-muted mb-0">
              Please wait while we process your request
            </p>
          </div>
        </div>
      )}
    </Container>
  );
};

export default DepartmentManagement;
